/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type KeyboardEvent, type PropsWithChildren } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	floatingContainer: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	responsiveFloatingContainer: {
		width: '100%',
	},
	contentContainer: {
		height: '32px',
		fontVariantNumeric: 'tabular-nums',
	},
	separator: {
		backgroundColor: token('color.background.accent.gray.bolder'),
		height: '16px',
		width: '1px',
		borderRadius: token('radius.xsmall'),
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
	},
	separatorNew: {
		minWidth: '1px',
	},
	sideContentContainer: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	mainContainer: {
		pointerEvents: 'auto',
	},
});

export type FloatingToolbarContainerProps = PropsWithChildren<{
	innerRef?: React.RefObject<HTMLElement> | null; // remove optional on responsive_bulk_ops_toolbar_platform cleanup
	maxWidth?: number;
	onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
}>;

export const FloatingToolbarContainer = ({
	children,
	onKeyDown,
	innerRef,
	maxWidth,
}: FloatingToolbarContainerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			role="toolbar"
			xcss={cx(
				styles.floatingContainer,
				fg('responsive_bulk_ops_toolbar_platform') && styles.responsiveFloatingContainer,
			)}
			aria-label={formatMessage(messages.toolbarName)}
			onKeyDown={onKeyDown}
			ref={innerRef && fg('responsive_bulk_ops_toolbar_platform') ? innerRef : null}
			style={{ maxWidth: fg('responsive_bulk_ops_toolbar_platform') ? `${maxWidth}px` : undefined }}
		>
			{children}
		</Box>
	);
};

export const FloatingToolbarContent = ({
	children,
	innerRef, // remove optional on responsive_bulk_ops_toolbar_platform cleanup
}: PropsWithChildren & { innerRef?: React.RefObject<HTMLElement> | null }): React.JSX.Element => (
	<Inline
		xcss={styles.contentContainer}
		alignBlock="center"
		ref={innerRef && fg('responsive_bulk_ops_toolbar_platform') ? innerRef : null}
	>
		{children}
	</Inline>
);

export const FloatingToolbarSeparator = (): React.JSX.Element => (
	<Box
		xcss={cx(styles.separator, fg('responsive_bulk_ops_toolbar_platform') && styles.separatorNew)}
	/>
);

export const FloatingToolbarSideContent = ({
	children,
	innerRef, // remove optional on responsive_bulk_ops_toolbar_platform cleanup
}: PropsWithChildren & { innerRef?: React.RefObject<HTMLElement> | null }): React.JSX.Element => (
	<Box
		xcss={styles.sideContentContainer}
		ref={innerRef && fg('responsive_bulk_ops_toolbar_platform') ? innerRef : null}
	>
		{children}
	</Box>
);

export const ContentSlider = ({
	children,
	show,
}: {
	children: React.ReactNode;
	show: boolean;
}): React.JSX.Element => (
	<ExitingPersistence>
		{show && (
			<SlideIn enterFrom="bottom" fade="inout">
				{(slideInProps) => (
					<div {...slideInProps}>
						<Box xcss={styles.mainContainer}>{children}</Box>
					</div>
				)}
			</SlideIn>
		)}
	</ExitingPersistence>
);
